"use client";

import useDebounce from "@/hooks/debounce";
import { KeyboardEvent, useRef, useState } from "react";
import axios from "axios";
import {
  CircularProgress,
  Dialog,
  DialogContent,
  IconButton,
  Stack,
  Tooltip,
} from "@mui/material";
import SearchOutlinedIcon from "@mui/icons-material/SearchOutlined";
import SearchOffIcon from "@mui/icons-material/SearchOff";
import SearchIcon from "@mui/icons-material/Search";
import CloseIcon from "@mui/icons-material/Close";
import routes from "@/app.route";
import { useRouter } from "next/navigation";
import SparkleEffect from "./sparkle-effect";
import config from "@/app.config";

export default function Search() {
  const router = useRouter();
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const { debounce } = useDebounce();
  const inputRef = useRef<HTMLInputElement>(null);
  const [data, setData] = useState<
    {
      id: string | number;
      handle: string;
      title: string;
    }[]
  >([]);

  const cancel = () => {
    setOpen(false);
    setQuery("");
    setData([]);
  };

  async function handleSearch(e: React.ChangeEvent<HTMLInputElement>) {
    setQuery(e.target.value);

    if (!e.target.value.trim()) {
      setData([]);
      return;
    }

    setLoading(true);

    debounce(async () => {
      const response = await axios.get(
        `${config.app.apiBaseUrl}/api/v2/search?q=${encodeURIComponent(e.target.value)}`,
      );

      setData(response.data.data);

      setLoading(false);
    }, 500);
  }

  function handleKeyDown(e: KeyboardEvent<HTMLInputElement>) {
    if (e.key === "Enter" && query.trim()) {
      e.preventDefault();
      router.push(`${routes.search.url}?q=${encodeURIComponent(query)}`);
    }
  }

  return (
    <>
      <SparkleEffect>
        <Tooltip title="Search">
          <IconButton aria-label="Open search" onClick={() => setOpen(true)}>
            <SearchOutlinedIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </SparkleEffect>
      <Dialog
        open={open}
        onClose={cancel}
        maxWidth="sm"
        fullWidth
        slotProps={{
          transition: {
            onEntered: () => {
              inputRef.current?.focus();
            },
          },
        }}
      >
        <div className="bg-zinc-50 border-b border-zinc-300 flex items-center px-4 h-[50px] gap-x-3 shrink-0">
          <SearchIcon color="inherit" className="text-zinc-500" />
          <input
            ref={inputRef}
            value={query}
            onChange={handleSearch}
            onKeyDown={handleKeyDown}
            placeholder="Search products"
            className="w-full focus-visible:outline-none"
          />
          {query.length > 0 && (
            <IconButton
              onClick={() => {
                setQuery("");
                setData([]);
              }}
            >
              <CloseIcon fontSize="small" color="inherit" />
            </IconButton>
          )}
        </div>
        <DialogContent>
          {loading ? (
            <Stack justifyContent="center" direction="row">
              <CircularProgress size="3rem" />
            </Stack>
          ) : data.length == 0 && query.length == 0 ? (
            <div className="py-4 text-center text-zinc-500">
              No recent searches
            </div>
          ) : (
            data.length == 0 && (
              <div className="flex items-center flex-col gap-2">
                <SearchOffIcon className="size-[60px]!" color="disabled" />
                <div className="mb-3">
                  <p className="text-lg text-center lg:text-xl">
                    No results for <strong>&apos;{query}&apos;</strong>
                  </p>
                  <p className="text-center text-zinc-500 text-sm">
                    Try searching for another keywords.
                  </p>
                </div>
              </div>
            )
          )}

          {data.length > 0 && (
            <div className="text-sm [&_em]:bg-yellow-200 [&_em]:not-italic">
              {data.map((value, index) => (
                <div
                  className="block hover:bg-zinc-100 p-3 rounded-md cursor-pointer"
                  onClick={() => {
                    router.push(
                      routes.product.url.replace(":slug", value.handle),
                    );
                    cancel();
                  }}
                  key={index}
                >
                  <div className="font-medium">{value.title}</div>
                </div>
              ))}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
